import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { LabelType, useLabelsQuery, LabelOrigin } from 'graphql/types';
import {
  TimesheetLabel,
  CustomLabel
} from 'modules/timesheet-labels/labelSchema';
import { useMeContext } from 'modules/me';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { mapBasicLabel, mapSelectLabel } from './labelMapUtils';

export type TimesheetLabelType = {
  loading: boolean;
  customLabels: CustomLabel[];
  timesheetLabels: TimesheetLabel[];
  showStartEndTime: boolean;
};

export const useLabels = (
  forDate: DateTime,
  disableSuggestions?: boolean
): TimesheetLabelType => {
  const isSmartSuggestionEnabled =
    useIsSmartSuggestionSettingEnabled() && !disableSuggestions;
  const me = useMeContext();

  const { data, loading } = useLabelsQuery({
    variables: {
      date: forDate.toISODate()
    },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });

  const labels = useMemo(
    () =>
      ((data && data.labels?.items) || []).map((label) =>
        label.type === LabelType.Select
          ? mapSelectLabel(isSmartSuggestionEnabled, label)
          : mapBasicLabel(label, isSmartSuggestionEnabled)
      ),
    [data, isSmartSuggestionEnabled]
  );

  const showStartEndTime = useMemo(() => {
    return me?.showStartEndFields || false;
  }, [me?.showStartEndFields]);

  const { timesheetLabels, customLabels } = useMemo(() => {
    if (labels) {
      const timesheetLabels = labels.filter(
        (label) => label.origin === LabelOrigin.Timesheet || !label.origin
      );
      const customLabels = labels
        .filter((label) => label.origin === LabelOrigin.User)
        .sort((a, b) => a.name.localeCompare(b.name));
      return { timesheetLabels, customLabels };
    }
    return { timesheetLabels: [], customLabels: [] };
  }, [labels]);

  return useMemo(
    () => ({
      loading: loading,
      customLabels,
      timesheetLabels,
      showStartEndTime
    }),
    [loading, customLabels, timesheetLabels, showStartEndTime]
  );
};
